import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { eventReportBlocker, eventReportScreen, reportPostIdOf, subjectFromUnifiedEvent } from '../../services/eventReport';
import { navigateTo } from '../../navigation/rootNavigation';
import type { UnifiedEvent } from '../../types/auth';
import { ON_ACCENT_TEXT } from '../../styles/accentText';
import { fontWeights } from '../../styles/typographyTokens';

interface Props {
  event: UnifiedEvent;
  /** Закрыть окно события перед переходом. */
  onLeave: () => void;
  textPrimary: string;
  textSecondary: string;
  isDark: boolean;
}

/**
 * CORE-1230 — «Рассказать, как прошло» на странице своего прошедшего
 * офлайн-события. Вход в отчёт без уведомления: страница события открывается
 * из анонса в своей ленте, из календаря и по ссылке.
 *
 * Кнопка видна только автору и только когда сервер отчёт примет (правила —
 * services/eventReport). Если отчёт уже есть и его номер известен, вместо
 * кнопки — «Отчёт опубликован» со ссылкой на него: второй форме взяться
 * неоткуда, а дубля сервер не примет.
 *
 * Ведёт на свою стену с композером в режиме отчёта — форма одна, та же, что
 * для обычной публикации.
 */
export const EventReportOffer: React.FC<Props> = ({ event, onLeave, textPrimary, textSecondary, isDark }) => {
  const { user, hasRole } = useAuth();
  const { t } = useI18n('common');
  const myMasterProfileId = hasRole('Master') ? user?.masterProfileId ?? null : null;
  const eligible = event.source === 'master'
    && eventReportBlocker(subjectFromUnifiedEvent(event), { myMasterProfileId, now: Date.now() }) === null;
  const [reportPostId, setReportPostId] = useState<number | null>(null);

  useEffect(() => {
    setReportPostId(null);
    if (!eligible || myMasterProfileId == null) return;
    let cancelled = false;
    // Каталог событий отчёт не знает — его номер отдаёт только ручка профиля
    // мастера. Не ответила — показываем кнопку: сервер на публикации скажет,
    // если отчёт уже есть.
    api.getMasterEvent(myMasterProfileId, event.id)
      .then((ev) => { if (!cancelled) setReportPostId(reportPostIdOf(ev)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eligible, myMasterProfileId, event.id]);

  if (!eligible) return null;

  if (reportPostId != null) {
    return (
      <View testID="event-report-published" style={styles.publishedRow}>
        <Ionicons name="checkmark-circle-outline" size={18} color={isDark ? '#68D391' : '#2F855A'} />
        <Text style={[styles.publishedText, { color: textSecondary }]}>{t('eventReport.published')}</Text>
        <TouchableOpacity
          testID="event-report-open"
          accessibilityRole="link"
          hitSlop={8}
          onPress={() => {
            onLeave();
            if (user?.id) navigateTo('PublicUserPost', { userId: user.id, postId: reportPostId });
          }}
        >
          <Text style={[styles.publishedLink, { color: textPrimary }]}>{t('eventReport.openReport')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        testID="event-report-tell"
        accessibilityRole="button"
        accessibilityHint={t('eventReport.tellHint')}
        activeOpacity={0.85}
        onPress={() => {
          onLeave();
          const to = eventReportScreen(event.id);
          navigateTo(to.name, to.params);
        }}
        style={styles.tellBtn}
      >
        <Ionicons name="create-outline" size={18} color={ON_ACCENT_TEXT} />
        <Text style={[styles.tellText, { color: ON_ACCENT_TEXT }]}>{t('eventReport.tell')}</Text>
      </TouchableOpacity>
      <Text style={[styles.hint, { color: textSecondary }]}>{t('eventReport.tellHint')}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { gap: 6, marginTop: 22 },
  tellBtn: {
    minHeight: 48,
    borderRadius: 14,
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E98245',
  },
  tellText: { fontSize: 16, fontWeight: fontWeights.bold },
  hint: { fontSize: 13, textAlign: 'center' },
  publishedRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 22 },
  publishedText: { fontSize: 14 },
  publishedLink: { fontSize: 14, fontWeight: fontWeights.bold, textDecorationLine: 'underline' },
});
